/**
 * Supervisor Test Runner — pytest από το UI.
 *
 * - Λίστα με όλα τα test files (backend/tests/*.py)
 * - Επιλογή αρχείων (checkbox) + Run selected / Run all
 * - Live status polling (2s) → πράσινο/κόκκινο dashboard
 * - Expand per-test outcomes για failures
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Play, RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle,
  ChevronRight, ChevronDown, ListChecks, FileCode2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { supervisorApi } from "@/lib/SupervisorApi";

const OUTCOME_STYLES = {
  passed: "bg-emerald-900/40 text-emerald-300 border-emerald-800",
  failed: "bg-rose-900/40 text-rose-300 border-rose-800",
  skipped: "bg-zinc-800 text-zinc-400 border-zinc-700",
  error: "bg-rose-900/60 text-rose-200 border-rose-700",
  xfailed: "bg-amber-900/40 text-amber-300 border-amber-800",
  xpassed: "bg-cyan-900/40 text-cyan-300 border-cyan-800",
};

function fmtDuration(sec) {
  if (sec == null) return "—";
  if (sec < 1) return `${Math.round(sec * 1000)}ms`;
  if (sec < 60) return `${sec.toFixed(2)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return `${m}m ${s}s`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("el-GR", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function TestRunnerTab() {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);   // full details object
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filter, setFilter] = useState("");
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [flist, rlist] = await Promise.all([
        supervisorApi.listTests(),
        supervisorApi.listRuns(),
      ]);
      setFiles(flist.data.files || []);
      setRuns(rlist.data.runs || []);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Poll while a run is in progress.
  const startPolling = useCallback((runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const [runResp, listResp] = await Promise.all([
          supervisorApi.getRun(runId),
          supervisorApi.listRuns(),
        ]);
        const rec = runResp.data;
        setActiveRun(rec);
        setRuns(listResp.data.runs || []);
        if (rec.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          const s = rec.summary || {};
          if (rec.status === "error") {
            toast.error(`Το run τερμάτισε με σφάλμα (exit ${rec.exit_code})`);
          } else if (s.failed) {
            toast.error(`${s.failed} tests απέτυχαν — ${s.passed}/${s.total} πέρασαν`);
          } else {
            toast.success(`Όλα OK! ${s.passed}/${s.total} passed σε ${fmtDuration(rec.duration)}`);
          }
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunning(false);
        toast.error("Αποτυχία polling: " + (e?.response?.data?.detail || e.message));
      }
    }, 2000);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const kickoffRun = useCallback(async (fileList) => {
    setRunning(true);
    setExpandedRunId(null);
    try {
      const { data } = await supervisorApi.runTests(fileList);
      toast.info(`Ξεκίνησε run ${data.run_id.slice(0, 6)}…`);
      setActiveRun({
        id: data.run_id, status: "running", summary: null, tests: [],
        started_at: new Date().toISOString(), files: fileList || [],
      });
      setExpandedRunId(data.run_id);
      startPolling(data.run_id);
    } catch (e) {
      setRunning(false);
      toast.error("Αποτυχία εκκίνησης: " + (e?.response?.data?.detail || e.message));
    }
  }, [startPolling]);

  const runSelected = () => kickoffRun(Array.from(selected));
  const runAll = () => kickoffRun([]);

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filteredFiles.length) setSelected(new Set());
    else setSelected(new Set(filteredFiles));
  };

  const filteredFiles = useMemo(() => {
    if (!filter.trim()) return files;
    const q = filter.trim().toLowerCase();
    return files.filter((f) => f.toLowerCase().includes(q));
  }, [files, filter]);

  const openRun = useCallback(async (runId) => {
    if (expandedRunId === runId) { setExpandedRunId(null); return; }
    setExpandedRunId(runId);
    try {
      const { data } = await supervisorApi.getRun(runId);
      setActiveRun(data);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης run: " + (e?.response?.data?.detail || e.message));
    }
  }, [expandedRunId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 py-8">
        <RefreshCw className="animate-spin" size={16} /> Φόρτωση…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ListChecks size={18} className="text-amber-400" />
            <h3 className="font-heading font-bold text-lg">Test Suite</h3>
            <span className="text-xs text-zinc-500">
              {selected.size}/{filteredFiles.length} επιλεγμένα · {files.length} σύνολο
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadAll}
              data-testid="tr-refresh"
              className="h-8 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-bold flex items-center gap-1"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              onClick={runSelected}
              disabled={running || selected.size === 0}
              data-testid="tr-run-selected"
              className="h-8 px-3 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-xs font-bold flex items-center gap-1"
            >
              <Play size={12} /> Run επιλεγμένων ({selected.size})
            </button>
            <button
              onClick={runAll}
              disabled={running}
              data-testid="tr-run-all"
              className="h-8 px-3 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-xs font-bold flex items-center gap-1"
            >
              <Play size={12} /> Run ALL ({files.length})
            </button>
          </div>
        </div>

        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Φιλτράρισμα ονόματος αρχείου…"
          data-testid="tr-file-filter"
          className="w-full h-9 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-sm text-zinc-100 mb-3 focus:border-amber-500 outline-none"
        />

        <div className="border border-zinc-800 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
          <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 px-3 py-2 flex items-center gap-3 text-xs font-bold text-zinc-400">
            <input
              type="checkbox"
              checked={selected.size === filteredFiles.length && filteredFiles.length > 0}
              onChange={toggleAll}
              data-testid="tr-toggle-all"
              className="w-4 h-4 accent-amber-500"
            />
            <span className="flex-1">Αρχείο</span>
          </div>
          {filteredFiles.length === 0 && (
            <div className="p-4 text-center text-zinc-500 text-sm">Δεν βρέθηκαν αρχεία.</div>
          )}
          {filteredFiles.map((f) => (
            <label
              key={f}
              className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-800/50 cursor-pointer border-b border-zinc-800/50 last:border-0"
              data-testid={`tr-file-${f}`}
            >
              <input
                type="checkbox"
                checked={selected.has(f)}
                onChange={() => toggle(f)}
                className="w-4 h-4 accent-amber-500"
              />
              <FileCode2 size={14} className="text-zinc-500" />
              <span className="text-sm font-mono text-zinc-200">{f}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Runs history */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-amber-400" />
          <h3 className="font-heading font-bold text-lg">Ιστορικό Runs</h3>
          <span className="text-xs text-zinc-500">(τελευταία 20)</span>
        </div>

        {runs.length === 0 && (
          <div className="text-zinc-500 text-sm py-4">
            Δεν έχει τρέξει κανένα test ακόμη. Επίλεξε αρχεία και πάτησε «Run».
          </div>
        )}

        <div className="space-y-2">
          {runs.map((r) => (
            <RunRow
              key={r.id}
              run={r}
              expanded={expandedRunId === r.id}
              onToggle={() => openRun(r.id)}
              detail={expandedRunId === r.id ? activeRun : null}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function RunRow({ run, expanded, onToggle, detail }) {
  const s = run.summary || {};
  const isRunning = run.status === "running";
  const failed = (s.failed || 0) + (s.error || 0);
  const passed = s.passed || 0;
  const total = s.total || 0;
  const pill = isRunning
    ? "bg-cyan-900/40 text-cyan-300 border-cyan-800"
    : run.status === "error"
      ? "bg-rose-900/60 text-rose-200 border-rose-700"
      : failed > 0
        ? "bg-rose-900/40 text-rose-300 border-rose-800"
        : "bg-emerald-900/40 text-emerald-300 border-emerald-800";

  const PillIcon = isRunning
    ? Loader2
    : run.status === "error"
      ? AlertTriangle
      : failed > 0
        ? XCircle
        : CheckCircle2;

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        data-testid={`tr-run-row-${run.id}`}
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-800/50 text-left"
      >
        {expanded ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronRight size={14} className="text-zinc-500" />}
        <span className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase flex items-center gap-1 ${pill}`}>
          <PillIcon size={11} className={isRunning ? "animate-spin" : ""} />
          {isRunning ? "RUNNING" : run.status === "error" ? "ERROR" : failed > 0 ? "FAIL" : "PASS"}
        </span>
        <span className="font-mono text-xs text-zinc-400">{run.id.slice(0, 8)}</span>
        <span className="text-xs text-zinc-500">{fmtTime(run.started_at)}</span>
        <span className="text-xs text-zinc-500">{fmtDuration(run.duration)}</span>
        <span className="flex-1 text-right text-xs">
          {total > 0 && (
            <>
              <span className="text-emerald-400 font-bold">{passed}</span>
              <span className="text-zinc-600">/</span>
              <span className="text-zinc-300 font-bold">{total}</span>
              {failed > 0 && (
                <span className="text-rose-400 font-bold ml-2">✗{failed}</span>
              )}
              {s.skipped > 0 && (
                <span className="text-zinc-500 font-bold ml-2">·{s.skipped} skipped</span>
              )}
            </>
          )}
        </span>
      </button>

      {expanded && detail && (
        <div className="border-t border-zinc-800 bg-zinc-950/50 p-4 space-y-3">
          <div className="text-xs text-zinc-500">
            Files: {run.files?.length ? run.files.join(", ") : "όλα"}
            {run.started_by && <span className="ml-3">· από: <span className="text-zinc-300">{run.started_by}</span></span>}
          </div>

          {isRunning && (
            <div className="flex items-center gap-2 text-cyan-300 text-sm py-2">
              <Loader2 className="animate-spin" size={14} /> Το test τρέχει… (polling κάθε 2s)
            </div>
          )}

          {detail.tests?.length > 0 && (
            <div className="max-h-80 overflow-y-auto border border-zinc-800 rounded-lg">
              {detail.tests.map((t, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 border-b border-zinc-800/50 last:border-0 flex items-start gap-3"
                  data-testid={`tr-test-${idx}`}
                >
                  <span className={`shrink-0 px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase ${OUTCOME_STYLES[t.outcome] || OUTCOME_STYLES.error}`}>
                    {t.outcome}
                  </span>
                  <span className="shrink-0 text-xs text-zinc-500 font-mono">{fmtDuration(t.duration)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-zinc-300 break-all">{t.nodeid}</div>
                    {t.outcome !== "passed" && t.message && (
                      <pre className="mt-1 text-[11px] text-rose-300/80 bg-rose-950/20 border border-rose-900/40 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                        {t.message}
                      </pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {detail.log_tail && (
            <details className="text-xs">
              <summary className="cursor-pointer text-zinc-400 hover:text-zinc-200 font-bold">
                Full pytest output ({detail.log_tail.length} chars)
              </summary>
              <pre className="mt-2 p-3 bg-zinc-950 border border-zinc-800 rounded text-[11px] text-zinc-300 overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">
                {detail.log_tail}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
